import React, { useEffect, useRef, useState } from 'react';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { CheckCircle, Cancel, InsertDriveFile } from '@material-ui/icons';
import { DropzoneAreaBase } from 'material-ui-dropzone';
import Swal from 'sweetalert2';
import { TermValidationService } from '../../../../services/term-validation.service';

export function DocumentUploadModal({
  open,
  documentName,
  requireValidation,
  onClose,
  onUpload,
}) {
  const [file, setFile] = useState(null);
  const [checks, setChecks] = useState([]);
  const [validationApproved, setValidationApproved] = useState(false);
  const [validationSummary, setValidationSummary] = useState('');
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [uploading, setUploading] = useState(false);
  const validationRequestRef = useRef(0);

  useEffect(() => {
    if (!open) {
      validationRequestRef.current += 1;
      setFile(null);
      setChecks([]);
      setValidationApproved(false);
      setValidationSummary('');
      setValidationError('');
      setValidating(false);
      setUploading(false);
    }
  }, [open]);

  function handleDialogClose() {
    if (validating) return;
    onClose();
  }

  async function handleCancel() {
    if (validating) {
      const { isConfirmed } = await Swal.fire({
        icon: 'warning',
        title: 'Validação em andamento',
        text: 'Se desistir, o modal será fechado, mas a chamada continuará sendo processada.',
        showCancelButton: true,
        confirmButtonText: 'Desistir',
        cancelButtonText: 'Continuar aguardando',
        confirmButtonColor: '#FE5D00',
        allowOutsideClick: false,
      });

      if (!isConfirmed) return;
      validationRequestRef.current += 1;
    }

    onClose();
  }

  async function handleAddFile(files) {
    const selectedFile = files[0]?.file;
    if (!selectedFile) return;

    setFile(selectedFile);
    setChecks([]);
    setValidationApproved(false);
    setValidationSummary('');
    setValidationError('');
    if (!requireValidation) {
      setValidating(false);
      return;
    }

    setValidating(true);
    const requestId = ++validationRequestRef.current;

    try {
      const result = await TermValidationService.validate(selectedFile);
      if (requestId !== validationRequestRef.current) return;

      const hasExpectedFormat =
        result &&
        result.ok === true &&
        typeof result.approved === 'boolean' &&
        Array.isArray(result.checks) &&
        result.checks.length > 0 &&
        result.checks.every(
          (check) =>
            check &&
            typeof check.label === 'string' &&
            typeof check.passed === 'boolean' &&
            typeof check.detail === 'string'
        );

      if (!hasExpectedFormat) {
        setValidationError(
          'Não foi possível interpretar o resultado da validação.'
        );
        return;
      }

      setChecks(
        result.checks.map((check) => ({
          label: check.label,
          value: check.detail,
          valid: check.passed,
        }))
      );
      setValidationApproved(result.approved);
      setValidationSummary(
        typeof result.summary === 'string' ? result.summary : ''
      );
    } catch (error) {
      if (requestId !== validationRequestRef.current) return;

      setValidationError(
        'Não foi possível validar o documento. Tente novamente.'
      );
    } finally {
      if (requestId === validationRequestRef.current) {
        setValidating(false);
      }
    }
  }

  const isValidationApproved = !requireValidation || validationApproved;
  const canUpload = Boolean(
    file && isValidationApproved && !validating && !uploading
  );
  const hasValidationFailure =
    requireValidation &&
    (Boolean(validationError) ||
      (checks.length > 0 && !validationApproved));
  const fileIconColor = !file
    ? '#000000'
    : hasValidationFailure
      ? '#f44336'
      : validating || (requireValidation && checks.length === 0)
        ? '#6C6C6C'
        : '#07C8A9';

  async function handleUpload() {
    if (!canUpload) return;

    setUploading(true);
    const uploaded = await onUpload(file);
    setUploading(false);

    if (uploaded) onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: {
          height:
            requireValidation && checks.length > 0
              ? 'min(560px, 90vh)'
              : 'auto',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      aria-labelledby="document-upload-dialog-title"
    >
      <DialogTitle id="document-upload-dialog-title">
        Enviar <strong>{documentName}</strong>
      </DialogTitle>
      <DialogContent
        className="profile-document-dialog-content"
        style={{ position: 'relative' }}
      >
        {requireValidation && validating && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
            }}
          >
            <CircularProgress style={{ color: '#FE5D00' }} />
            <Typography>Validando documento...</Typography>
          </div>
        )}
        {open && (
          <DropzoneAreaBase
            acceptedFiles={['.pdf', 'application/pdf', '.png', '.jpg', '.jpeg']}
            showFileNames
            dropzoneClass="profile-document-dropzone"
            dropzoneText={
              <>
                <InsertDriveFile
                  style={{ fontSize: 64, color: fileIconColor }}
                />
                <span>
                  {file
                    ? file.name
                    : 'Arraste o documento ou clique para selecionar'}
                </span>
              </>
            }
            showAlerts={false}
            filesLimit={1}
            maxFileSize={1000000000}
            onAdd={handleAddFile}
          />
        )}

        {requireValidation && validationError && (
          <Typography color="error" style={{ marginTop: 16 }}>
            {validationError}
          </Typography>
        )}

        {requireValidation && checks.length > 0 && (
          <>
            {validationSummary && (
              <Typography style={{ marginTop: 16 }}>
                {validationSummary}
              </Typography>
            )}
            <List className="profile-document-checks">
              {checks.map((check) => (
                <ListItem
                  key={check.label}
                  className="profile-document-check"
                  divider
                  dense
                  disableGutters
                >
                  <ListItemIcon>
                    {check.valid ? (
                      <CheckCircle style={{ color: '#07C8A9' }} />
                    ) : (
                      <Cancel color="error" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={`${check.label}: ${check.value || 'Não encontrado'}`}
                    primaryTypographyProps={{
                      style: { color: check.valid ? '#6C6C6C' : '#f44336' },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          color="default"
          disabled={uploading}
          style={{ textTransform: 'none' }}
        >
          Cancelar
        </Button>
        <Button
          className="profile-document-send-button"
          onClick={handleUpload}
          variant="contained"
          disabled={!canUpload}
          style={{
            minWidth: 132,
            minHeight: 40,
            margin: '0 8px 0 0',
            borderRadius: 6,
            backgroundColor: '#FE5D00',
            color: '#FFFFFF',
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          {uploading ? 'Enviando...' : 'Enviar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
